import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '@src/redux/store';
import { Payment } from '@src/types/payment';
import type { Project } from '@src/types/project';
import {
  createPaymentThunk,
  previewPaymentThunk,
  updatePaymentThunk,
} from './paymentThunk';
import {
  getPaymentByIdThunk,
  getProjectDetailThunk,
  previewPaymentWithoutPaymentIdThunk,
  updateProjectData,
} from './projectThunk';
import { ProjectDetailInfo } from '../types';

export interface ProjectDetailState extends Project {
  isCancelConfirmModalOpen?: boolean;
  isEditEstimate?: boolean;
  isPreviewModalOpen?: boolean;
  isReEstimate?: boolean;
  isPatchedReEstimate?: boolean;
  /** 재견적 confirm 모달 상태값 */
  isReEstimateModalOpen?: boolean;
  /** 견적 초기화 모달 상태값 */
  isResetModalOpen?: boolean;
  /** 캠페인 상태 변경시 alert 모달 상태값  */
  isMailAlertModalOpen?: boolean;
  isSearchLoading?: boolean;

  campaignIds: number[];
}

export const projectInitialState: ProjectDetailState = {
  isCancelConfirmModalOpen: false,
  isEditEstimate: false,
  isPreviewModalOpen: false,
  isReEstimate: false,
  isPatchedReEstimate: false,
  isReEstimateModalOpen: false,
  isResetModalOpen: false,
  isMailAlertModalOpen: false,
  isSearchLoading: false,

  id: 0,
  serviceId: '',
  companyId: 0,
  userId: 0,
  type: 'brandSeed',
  name: '',
  brandKo: '',
  brandEn: '',
  productName: '',
  productWeight: 0,
  productCost: '',
  influencerType: 'allRounder',
  prototypeNote: '',
  status: 'new',
  shippingMethod: 'charis',
  purchaseUrl: '',
  description: '',
  charisCampaignId: '',
  company: {
    id: '',
    name: '',
    avatarUrl: '',
    businessRegistrationNumber: '',
    representative: '',
    businessConditions: '',
    businessCategory: '',
    accountant: '',
    accountantEmail: '',
    accountantMobileNumber: '',
    remainCash: '',
    note: '',
  },
  user: {
    name: '',
    mobileNumber: '',
    role: 'user',
  },
  adminUsers: [],
  mainAdminUser: { name: '', mobileNumber: '', role: 'admin' },
  campaigns: [],
  products: [],
  payment: {
    id: '',
    projectId: 0,
    companyId: 0,
    status: 'pending',
    paymentItems: [],
    subtotalAmount: '',
    promotions: [],
    promotionDiscountAmount: '',
    couponDiscountAmount: '',
    paymentSubtotalAmount: '',
    chargedAmount: '',
    refundableAmount: '',
    refundedAmount: '',
    availablePromotions: [],
    availableCoupons: [],
  },
  orders: [],

  campaignIds: [],
};

const projectSlice = createSlice({
  name: 'project',
  initialState: projectInitialState,
  reducers: {
    onChangeProjectStatusCancel: (state) => {
      state.status = 'cancel';
    },
    onChangeCancelConfirmModal: (state) => {
      state.isCancelConfirmModalOpen = !state.isCancelConfirmModalOpen;
    },
    onEditEstimateModalOpen: (state) => {
      state.isEditEstimate = !state.isEditEstimate;
    },
    /** paymentItem의 value를 수정하기 위한 action */
    onChangeCampaignPrice: (
      state,
      {
        payload,
      }: PayloadAction<{
        id?: string;
        localId?: string;
        target: 'name' | 'promotionName' | 'amount' | 'discountAmount';
        value?: string;
        price?: string;
      }>
    ) => {
      const targetId = payload.localId ? 'localId' : 'id';

      const targetItem = state.payment.paymentItems.find(
        (element) => element[targetId] === payload[targetId]
      );

      if (payload.target === 'name' || payload.target === 'promotionName') {
        targetItem[payload.target] = payload.value;

        return;
      }

      targetItem[payload.target] = payload.price;
    },
    /** payment의 재견적 여부를 확인하기 위한 action */
    onChangeIsReEstimate: (
      state,
      {
        payload,
      }: PayloadAction<{
        id?: string;
        localId?: string;
        target: 'name' | 'promotionName' | 'amount' | 'discountAmount';
        value?: string;
        price?: string;
      }>
    ) => {
      if (!payload.id) {
        return;
      }

      const targetItem = state.payment.paymentItems.find(
        (element) => element.id === payload.id
      );

      const checkIsReEstimate = JSON.parse(
        sessionStorage.getItem('cachedData')
      ).find((element) => element.id === payload.id);

      if (payload.target === 'name' || payload.target === 'promotionName') {
        state.isReEstimate =
          targetItem[payload.target] !== checkIsReEstimate[payload.target];
        return;
      }

      state.isReEstimate =
        targetItem[payload.target] !==
        Number(checkIsReEstimate[payload.target]).toString();
    },
    onIsPreviewModalOpen: (state) => {
      state.isPreviewModalOpen = !state.isPreviewModalOpen;
    },
    onReEstimateModalOpen: (state) => {
      state.isReEstimateModalOpen = !state.isReEstimateModalOpen;
    },
    onResetModalOpen: (state) => {
      state.isResetModalOpen = !state.isResetModalOpen;
    },
    onMailAlertModalOpen: (state) => {
      state.isMailAlertModalOpen = !state.isMailAlertModalOpen;
    },
    onDeletePaymentItem: (state, { payload }) => {
      state.isReEstimate = true;

      state.payment.paymentItems = state.payment.paymentItems.filter(
        (element) => {
          if (element.id) {
            return element.id !== payload?.id;
          }
          return element.localId !== payload.localId;
        }
      );
    },
    onPlusPaymentItemAction: (state, { payload }) => {
      state.isReEstimate = true;

      state.payment.paymentItems.push(payload);
    },
    onChangeSubmitData: (state, { payload }) => {
      state.adminUsers = payload.adminUsers;
      state.brandEn = payload.brandEn;
      state.brandKo = payload.brandKo;
      state.influencerType = payload.influencerType;
      state.mainAdminUser = payload.mainAdminUser;
      state.name = payload.name;
      state.productName = payload.productName;
      state.productWeight = payload.productWeight;
      state.status = payload.status;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getProjectDetailThunk.fulfilled,
      (state, { payload }: PayloadAction<Project>) => {
        const {
          updatedAt,
          id,
          serviceId,
          companyId,
          userId,
          type,
          name,
          brandKo,
          brandEn,
          productName,
          productWeight,
          productCost,
          influencerType,
          prototypeNote,
          status,
          shippingMethod,
          shippingStatus,
          purchaseUrl,
          description,
          files,
          charisCampaignId,
          company,
          user,
          adminUsers,
          mainAdminUser,
          campaigns,
          products,
          payment,
          orders,
        } = payload;

        sessionStorage.setItem(
          'cachedData',
          JSON.stringify(payment?.paymentItems)
        );

        const campaignIds = payment?.paymentItems
          ? [
              ...new Set(
                payment.paymentItems
                  .filter((element) => element.campaign)
                  .map((element) => {
                    return element.campaign.id;
                  })
              ),
            ]
          : [];

        state.id = id;
        state.companyId = companyId;
        state.status = status;
        state.type = type;
        state.company = company;
        state.updatedAt = updatedAt;
        state.user = user;
        state.adminUsers = adminUsers;
        state.mainAdminUser = mainAdminUser;
        state.charisCampaignId = charisCampaignId;
        state.serviceId = serviceId;
        state.userId = userId;
        state.name = name;
        state.brandKo = brandKo;
        state.brandEn = brandEn;
        state.productName = productName;
        state.productWeight = productWeight;
        state.productCost = productCost;
        state.influencerType = influencerType;
        state.prototypeNote = prototypeNote;
        state.shippingMethod = shippingMethod;
        state.shippingStatus = shippingStatus;
        state.purchaseUrl = purchaseUrl;
        state.description = description;
        state.files = files;
        state.campaigns = campaigns;
        state.products = products;
        state.orders = orders;
        state.payment = payment;
        state.campaignIds = campaignIds;
      }
    );

    builder.addCase(
      updateProjectData.fulfilled,
      (state, { payload }: PayloadAction<Project>) => {
        const {
          updatedAt,
          id,
          serviceId,
          companyId,
          userId,
          type,
          name,
          brandKo,
          brandEn,
          productName,
          productWeight,
          productCost,
          influencerType,
          prototypeNote,
          status,
          shippingMethod,
          shippingStatus,
          purchaseUrl,
          description,
          files,
          charisCampaignId,
          company,
          user,
          adminUsers,
          mainAdminUser,
          campaigns,
          products,
          payment,
          orders,
        } = payload;

        state.id = id;
        state.companyId = companyId;
        state.status = status;
        state.type = type;
        state.company = company;
        state.updatedAt = updatedAt;
        state.user = user;
        state.adminUsers = adminUsers;
        state.mainAdminUser = mainAdminUser;
        state.charisCampaignId = charisCampaignId;
        state.serviceId = serviceId;
        state.userId = userId;
        state.name = name;
        state.brandKo = brandKo;
        state.brandEn = brandEn;
        state.productName = productName;
        state.productWeight = productWeight;
        state.productCost = productCost;
        state.influencerType = influencerType;
        state.prototypeNote = prototypeNote;
        state.shippingMethod = shippingMethod;
        state.shippingStatus = shippingStatus;
        state.purchaseUrl = purchaseUrl;
        state.description = description;
        state.files = files;
        state.campaigns = campaigns;
        state.products = products;
        state.orders = orders;
        state.payment = payment;
      }
    );
    builder.addCase(updateProjectData.rejected, (state, { payload }) => {
      throw payload;
    });

    builder.addCase(
      createPaymentThunk.fulfilled,
      (state, { payload }: PayloadAction<Payment>) => {
        sessionStorage.setItem(
          'cachedData',
          JSON.stringify(payload.paymentItems)
        );

        const campaignIds = payload?.paymentItems
          ? [
              ...new Set(
                payload.paymentItems
                  .filter((element) => element.campaign)
                  .map((element) => {
                    return element.campaign.id;
                  })
              ),
            ]
          : [];

        state.payment = payload;
        state.campaignIds = campaignIds;
        state.status = 'pendingPayment';
      }
    );

    builder.addCase(previewPaymentThunk.fulfilled, (state, { payload }) => {
      state.isPatchedReEstimate = true;
      state.isReEstimateModalOpen = false;
      state.isPreviewModalOpen = true;

      state.payment = payload;
    });

    builder
      .addCase(updatePaymentThunk.pending, () => {
        sessionStorage.removeItem('cachedData');
      })
      .addCase(updatePaymentThunk.fulfilled, (state, { payload }) => {
        const { paymentItems } = payload;
        state.isPatchedReEstimate = false;
        state.isReEstimate = false;

        sessionStorage.setItem('cachedData', JSON.stringify(paymentItems));

        state.payment = payload;
      });

    builder.addCase(
      previewPaymentWithoutPaymentIdThunk.fulfilled,
      (state, { payload }: PayloadAction<Payment>) => {
        const campaignIds = payload?.paymentItems
          ? [
              ...new Set(
                payload.paymentItems
                  .filter((element) => element.campaign)
                  .map((element) => {
                    return element.campaign.id;
                  })
              ),
            ]
          : [];

        state.payment = payload;
        state.campaignIds = campaignIds;
      }
    );

    builder.addCase(getPaymentByIdThunk.fulfilled, (state, { payload }) => {
      state.isPatchedReEstimate = false;
      state.isReEstimate = false;
      state.isResetModalOpen = false;

      state.payment = payload;
    });
  },
});

export type { ProjectDetailInfo };

export const {
  onChangeProjectStatusCancel,
  onChangeCancelConfirmModal,
  onChangeCampaignPrice,
  onEditEstimateModalOpen,
  onIsPreviewModalOpen,
  onReEstimateModalOpen,
  onResetModalOpen,
  onMailAlertModalOpen,
  onDeletePaymentItem,
  onPlusPaymentItemAction,
  onChangeSubmitData,
  onChangeIsReEstimate,
} = projectSlice.actions;
export default projectSlice.reducer;

const selfSelector = (state: RootState) => state.project;

export const projectSelector = createDraftSafeSelector(
  selfSelector,
  (projectState) => projectState
);

export const projectPaymentSelector = createDraftSafeSelector(
  selfSelector,
  (projectState) => projectState.payment
);
